import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import ArchiveIcon from '@mui/icons-material/Archive';
import { DateFormat } from '@startuptools/ui/DateFormat';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { IconButton } from '@startuptools/ui/IconButton';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Select } from '@startuptools/ui/Select';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Option } from '@startuptools/ui/Option';
import { Typography } from '@startuptools/ui/Typography';
import { useUpdateWarrantProgramMutation, WarrantProgramListViewDocument } from './warrant-programs.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import React from 'react';
import { GqlWarrantProgram, useDeleteWarrantProgramMutation } from '../../../graphql/react-operations';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { AlertConfirmDialog } from '../../../components/simple-dialogs/AlertConfirmDialog';
import { wpEditLink } from '../../../components/Links';
import { GqlWarrantProgramKind } from '../../../graphql/base-types.graphql';
import { styled } from '@mui/joy/styles';
import { Link } from 'react-router-dom';
import { useNavigate } from '../../../components/Navigate';

const DeletableReason = ({ reason }: { reason: string | null | undefined }) => {
  switch (reason) {
    case 'HAS_DOCUMENTS':
      return <Trans id="This program has generated documents attached to it." />;
    case 'HAS_STRIPE_INVOICE':
      return (
        <Trans id="This program has an open or paid invoice connected to it. Contact support if you want to delete this program." />
      );
    default:
      return null;
  }
};

export const WpTableRow = ({
  name,
  description,
  maxNumberOfOptions,
  subscriptionPrice,
  decisionDate,
  startDate,
  firstDateToSubscribeForShares,
  lastDateToSubscribeForShares,
  actions,
  header = false,
  linkTo,
  onClick,
}: {
  name?: React.ReactNode;
  description?: React.ReactNode;
  maxNumberOfOptions: React.ReactNode;
  subscriptionPrice?: React.ReactNode;
  decisionDate: React.ReactNode;
  startDate?: React.ReactNode;
  firstDateToSubscribeForShares: React.ReactNode;
  lastDateToSubscribeForShares: React.ReactNode;
  actions: React.ReactNode;
  header?: boolean;
  onClick?: () => void;
  linkTo?: string;
}) => {
  const T = header ? 'th' : 'td';
  const Tr = linkTo ? styled(Link)({ display: 'table-row' }) : 'tr';

  return (
    <Tr to={linkTo ?? ''} style={{ cursor: linkTo ? 'pointer' : undefined }} onClick={onClick}>
      <T>
        <Typography level="body-xs">{name}</Typography>
      </T>
      {description && (
        <T>
          <Typography level="body-xs">{description}</Typography>
        </T>
      )}
      {subscriptionPrice && (
        <T>
          <Typography level="body-xs">{subscriptionPrice}</Typography>
        </T>
      )}
      <T>
        <Typography level="body-xs">{maxNumberOfOptions}</Typography>
      </T>

      <T>
        <Typography level="body-xs">{decisionDate}</Typography>
      </T>
      {startDate && (
        <T>
          <Typography level="body-xs">{startDate}</Typography>
        </T>
      )}

      <T>
        <Typography level="body-xs">{firstDateToSubscribeForShares}</Typography>
      </T>
      <T>
        <Typography level="body-xs">{lastDateToSubscribeForShares}</Typography>
      </T>
      <T>
        <Typography level="body-xs">{actions}</Typography>
      </T>
    </Tr>
  );
};

export const OptionProgramsTable = ({
  status,
  setStatus,
  warrantPrograms,
  totalCount,
  warrantKind,
}: {
  status: 'active' | 'archived';
  setStatus: (status: 'active' | 'archived') => void;
  warrantPrograms: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'name'
    | 'description'
    | 'maxNumberOfOptions'
    | 'strikePrice'
    | 'decisionDate'
    | 'warrantStartDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
    | 'archived'
    | 'deletable'
    | 'usesStepper'
    | 'warrantKind'
  >[];
  totalCount: number;
  warrantKind: GqlWarrantProgramKind;
}) => {
  const companyId = useCompanyId();
  const { link } = useNavigate();
  // eslint-disable-next-line sut/loading-mutation
  const [deleteWp] = useDeleteWarrantProgramMutation({
    refetchQueries: [WarrantProgramListViewDocument],
  });

  // eslint-disable-next-line sut/loading-mutation
  const [updateWp] = useUpdateWarrantProgramMutation({
    refetchQueries: [WarrantProgramListViewDocument],
  });

  const { i18n } = useLingui();

  const [showAction, setShowAction] = React.useState<
    { id: string; action: 'delete' | 'archive' | 'unarchive' } | undefined
  >();

  const wpAction = showAction ? warrantPrograms.find(wp => wp.id === showAction.id) : undefined;

  const [_, startTransition] = React.useTransition();

  return (
    <>
      {Boolean(totalCount) && (
        <>
          <FormControl sx={{ maxWidth: '200px' }}>
            <FormLabel>
              <Trans id="Status" />
            </FormLabel>
            <Select
              sx={{ width: 'auto' }}
              value={status}
              onChange={(_e, value) => {
                startTransition(() => {
                  setStatus(value!);
                });
              }}
            >
              <Option value="active">
                <Trans id="Active" />
              </Option>
              <Option value="archived">
                <Trans id="Archived" />
              </Option>
            </Select>
          </FormControl>

          <Table hoverRow sx={{ minWidth: '1000px' }}>
            <thead>
              <WpTableRow
                name={<Trans id="Name" />}
                description={<Trans id="Description" />}
                maxNumberOfOptions={
                  warrantKind !== GqlWarrantProgramKind.Wise ? <Trans id="Maximum Number of Options" /> : undefined
                }
                subscriptionPrice={
                  warrantKind !== GqlWarrantProgramKind.Wise ? <Trans id="Subscription price" /> : undefined
                }
                decisionDate={<Trans id="Decision" />}
                startDate={<Trans id="Start date" />}
                firstDateToSubscribeForShares={<Trans id="Start subscription window" />}
                lastDateToSubscribeForShares={<Trans id="End subscription window" />}
                actions=""
                header
              />
            </thead>
            <tbody>
              {warrantPrograms.map(wp => {
                return (
                  <WpTableRow
                    key={wp.id}
                    linkTo={link(wpEditLink(wp))}
                    name={wp.name}
                    description={wp.description}
                    maxNumberOfOptions={
                      <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                        {wp.maxNumberOfOptions}
                      </NumberFormat>
                    }
                    subscriptionPrice={<NumberFormat locale={i18n.locale}>{wp.strikePrice}</NumberFormat>}
                    decisionDate={<DateFormat locale={i18n.locale}>{wp.decisionDate}</DateFormat>}
                    startDate={<DateFormat locale={i18n.locale}>{wp.warrantStartDate}</DateFormat>}
                    firstDateToSubscribeForShares={
                      <DateFormat locale={i18n.locale}>{wp.shareSubscribeStartDate}</DateFormat>
                    }
                    lastDateToSubscribeForShares={
                      <DateFormat locale={i18n.locale}>{wp.shareSubscribeEndDate}</DateFormat>
                    }
                    actions={
                      <Stack direction="row" gap={1} justifyContent="end">
                        <Tooltip title={wp.archived ? <Trans id="Unarchive" /> : <Trans id="Archive" />}>
                          <IconButton
                            variant="outlined"
                            color={wp.archived ? 'success' : 'warning'}
                            onClick={ev => {
                              ev.stopPropagation();
                              ev.preventDefault();
                              setShowAction({ id: wp.id, action: wp.archived ? 'unarchive' : 'archive' });
                            }}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={!wp.deletable.deletable ? <DeletableReason reason={wp.deletable.reason} /> : undefined}
                        >
                          <span>
                            <IconButton
                              variant="outlined"
                              color="danger"
                              disabled={!wp.deletable.deletable}
                              onClick={ev => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                setShowAction({ id: wp.id, action: 'delete' });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    }
                  />
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      {wpAction &&
        showAction &&
        (showAction.action === 'delete' ? (
          <AlertConfirmDialog
            onClose={() => {
              setShowAction(undefined);
            }}
            confirmButtonText={<Trans id="Delete" />}
            onConfirm={async () => {
              await deleteWp({ variables: { companyId, id: wpAction.id } });
              setShowAction(undefined);
            }}
            content={
              <Alert color="danger">
                <Trans
                  id="Do you really want to delete option program {name}?"
                  values={{ name: wpAction.name || wpAction.description }}
                />
              </Alert>
            }
          />
        ) : (
          <ConfirmDialog
            header={
              showAction.action === 'archive' ? (
                <Trans id="Archive option program" />
              ) : (
                <Trans id="Unarchive option program" />
              )
            }
            confirmProps={{
              children: showAction.action === 'archive' ? <Trans id="Archive" /> : <Trans id="Unarchive" />,
              color: 'danger',
            }}
            onCancel={() => {
              setShowAction(undefined);
            }}
            onConfirm={async () => {
              if (showAction.action === 'delete') {
                await deleteWp({ variables: { companyId, id: wpAction.id } });
              } else if (showAction.action === 'archive') {
                await updateWp({ variables: { companyId, input: { id: wpAction.id, archived: true } } });
              } else if (showAction.action === 'unarchive') {
                await updateWp({ variables: { companyId, input: { id: wpAction.id, archived: false } } });
              }
              setShowAction(undefined);
            }}
          >
            <Alert color="danger">
              {showAction.action === 'archive' ? (
                <Trans
                  id="Do you want to archive option program {name}? You can always unarchive it later."
                  values={{ name: wpAction.name || wpAction.description }}
                />
              ) : (
                <Trans
                  id="Do you want to unarchive option program {name}?"
                  values={{ name: wpAction.name || wpAction.description }}
                />
              )}
            </Alert>
          </ConfirmDialog>
        ))}
    </>
  );
};
